import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, parseISO, isAfter, isBefore, addDays } from 'date-fns';
import { toast } from 'react-hot-toast';
import { 
  PlusIcon, 
  MagnifyingGlassIcon, 
  FunnelIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  TagIcon
} from '@heroicons/react/24/outline';
import { tasksApi, Task, CreateTaskDto, TaskFilters } from '../api/tasks';
import { TaskType, TaskPriority, TaskStatus } from '@birdman/core';
import TaskModal from '../components/TaskModal';
import TaskViewModal from '../components/TaskViewModal';
import DeleteTaskModal from '../components/DeleteTaskModal';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import PageHero from '../components/ui/PageHero';
import EmptyState from '../components/ui/EmptyState';

interface TaskFiltersState {
  status?: Task['status'];
  type?: Task['type'];
  priority?: Task['priority'];
  assignedTo?: string;
  searchTerm?: string;
  overdue?: boolean;
  upcoming?: boolean;
}

export default function Tasks() {
  const { user } = useAuth();

  // Data state
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<TaskFiltersState>({});
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [sortBy, setSortBy] = useState<keyof Task>('dueDate');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);

  const handleRealtimeTask = useCallback((task: Task) => {
    setTasks(prev => {
      const existingIndex = prev.findIndex(t => t.id === task.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = task;
        return updated;
      }
      return [task, ...prev];
    });
  }, []);

  useWebSocket({
    userId: user?.id,
    autoConnect: !!user?.id,
    onTaskReceived: handleRealtimeTask,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await tasksApi.getAll();
      setTasks(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load task data');
      console.error('Error loading task data:', err);
      toast.error('Failed to load task data');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const getStatusBadge = (task: Task) => {
    const statusConfig = {
      PENDING: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Pending' },
      IN_PROGRESS: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'In Progress' },
      COMPLETED: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Completed' },
      CANCELLED: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Cancelled' },
      OVERDUE: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Overdue' },
      DEFERRED: { bg: 'bg-violet-100', text: 'text-violet-700', label: 'Deferred' },
      ESCALATED: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Escalated' }
    };

    const config = statusConfig[task.status] || statusConfig.PENDING;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getPriorityBadge = (task: Task) => {
    const priorityConfig = {
      LOW: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Low' },
      MEDIUM: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Medium' },
      HIGH: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'High' },
      URGENT: { bg: 'bg-rose-100', text: 'text-rose-800', label: 'Urgent' },
      CRITICAL: { bg: 'bg-rose-200', text: 'text-rose-900', label: 'Critical' }
    };

    const config = priorityConfig[task.priority] || priorityConfig.MEDIUM;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const getTypeBadge = (task: Task) => {
    const typeConfig = {
      BREEDING: { bg: 'bg-pink-100', text: 'text-pink-800', label: 'Breeding' },
      HEALTH_CHECK: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Health Check' },
      BANDING: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Banding' },
      FEEDING: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Feeding' },
      MAINTENANCE: { bg: 'bg-violet-100', text: 'text-violet-800', label: 'Maintenance' },
      GENERAL: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'General' },
      OTHER: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Other' }
    };

    const config = typeConfig[task.type] || typeConfig.OTHER;
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const isOverdue = (task: Task) => {
    return task.status !== 'COMPLETED' && 
           task.status !== 'CANCELLED' && 
           isAfter(new Date(), parseISO(task.dueDate));
  };

  const isUpcoming = (task: Task) => {
    const today = new Date();
    const nextWeek = addDays(today, 7);
    return task.status !== 'COMPLETED' && 
           task.status !== 'CANCELLED' && 
           isBefore(parseISO(task.dueDate), nextWeek) &&
           !isBefore(parseISO(task.dueDate), today);
  };

  // Filter and search logic
  const filteredTasks = useMemo(() => {
    let result = tasks;

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (task) =>
          task.title.toLowerCase().includes(term) ||
          task.description?.toLowerCase().includes(term) ||
          task.assignedTo?.toLowerCase().includes(term) ||
          task.id.toLowerCase().includes(term)
      );
    }

    // Apply filters
    if (filters.status) {
      result = result.filter((task) => task.status === filters.status);
    }

    if (filters.type) {
      result = result.filter((task) => task.type === filters.type);
    }

    if (filters.priority) {
      result = result.filter((task) => task.priority === filters.priority);
    }

    if (filters.assignedTo) {
      result = result.filter((task) => task.assignedTo === filters.assignedTo);
    }

    if (filters.overdue) {
      result = result.filter((task) => 
        task.status !== 'COMPLETED' && 
        task.status !== 'CANCELLED' && 
        isAfter(new Date(), parseISO(task.dueDate))
      );
    }

    if (filters.upcoming) {
      const today = new Date();
      const nextWeek = addDays(today, 7);
      result = result.filter((task) => 
        task.status !== 'COMPLETED' && 
        task.status !== 'CANCELLED' && 
        isBefore(parseISO(task.dueDate), nextWeek) &&
        !isBefore(parseISO(task.dueDate), today)
      );
    }

    return result;
  }, [tasks, searchTerm, filters]);

  // Sort tasks
  const sortedTasks = useMemo(() => {
    return [...filteredTasks].sort((a, b) => {
      let aValue: any = a[sortBy];
      let bValue: any = b[sortBy];

      if (sortBy === 'dueDate') {
        aValue = new Date(a.dueDate).getTime();
        bValue = new Date(b.dueDate).getTime();
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  }, [filteredTasks, sortBy, sortOrder]);

  // Pagination
  const paginatedTasks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedTasks.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedTasks, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedTasks.length / itemsPerPage);

  // Event handlers
  const handleAddTask = async (data: CreateTaskDto) => {
    try {
      await tasksApi.create(data);
      await loadData();
      setShowAddModal(false);
      toast.success('Task added successfully');
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    }
  };

  const handleUpdateTask = async (id: string, data: Partial<Task>) => {
    try {
      await tasksApi.update(id, data);
      await loadData();
      setShowEditModal(false);
      setSelectedTask(null);
      toast.success('Task updated successfully');
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async () => {
    if (!selectedTask) return;
    
    try {
      await tasksApi.delete(selectedTask.id);
      await loadData();
      setShowDeleteModal(false);
      setSelectedTask(null);
      toast.success('Task deleted successfully');
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleStatusChange = async (taskId: string, newStatus: Task['status']) => {
    try {
      await tasksApi.update(taskId, { status: newStatus });
      await loadData();
      toast.success('Task status updated');
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    }
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setCurrentPage(1);
  };

  const openViewModal = (task: Task) => {
    setSelectedTask(task);
    setShowViewModal(true);
  };

  const openEditModal = (task: Task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const openDeleteModal = (task: Task) => {
    setSelectedTask(task);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-medium">{error}</div>
        <button 
          onClick={loadData}
          className="mt-4 px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <PageHero
        title="Tasks"
        subtitle="Manage and track tasks across your facility"
        actions={
          <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            Add Task
          </button>
        }
      />

      {/* Filters and Search */}
      <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 max-w-lg">
          <div className="flex items-center w-full rounded-md border border-gray-300 bg-white shadow-sm focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500">
            <span className="pl-3 pr-2 flex items-center flex-shrink-0 text-slate-400" aria-hidden="true">
              <MagnifyingGlassIcon className="h-5 w-5" />
            </span>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 min-w-0 py-2 pr-3 border-0 bg-transparent text-sm placeholder-gray-500 focus:outline-none focus:ring-0"
              placeholder="Search tasks..."
            />
          </div>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 ${showFilters ? 'ring-2 ring-cyan-500' : ''}`}
          >
            <FunnelIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Filters
          </button>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-cyan-100 text-cyan-700' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-md ${viewMode === 'table' ? 'bg-cyan-100 text-cyan-700' : 'text-slate-400 hover:text-slate-600'}`}
            >
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="mt-4 bg-white p-4 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700">Status</label>
              <select
                value={filters.status || ''}
                onChange={(e) => setFilters({ ...filters, status: e.target.value as Task['status'] || undefined })}
                className="mt-1 input text-sm"
              >
                <option value="">All Status</option>
                <option value="PENDING">Pending</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
                <option value="OVERDUE">Overdue</option>
                <option value="DEFERRED">Deferred</option>
                <option value="ESCALATED">Escalated</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Type</label>
              <select
                value={filters.type || ''}
                onChange={(e) => setFilters({ ...filters, type: e.target.value as Task['type'] || undefined })}
                className="mt-1 input text-sm"
              >
                <option value="">All Types</option>
                <option value="BREEDING">Breeding</option>
                <option value="HEALTH_CHECK">Health Check</option>
                <option value="BANDING">Banding</option>
                <option value="FEEDING">Feeding</option>
                <option value="MAINTENANCE">Maintenance</option>
                <option value="GENERAL">General</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Priority</label>
              <select
                value={filters.priority || ''}
                onChange={(e) => setFilters({ ...filters, priority: e.target.value as Task['priority'] || undefined })}
                className="mt-1 input text-sm"
              >
                <option value="">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700">Special Filters</label>
              <div className="mt-1 space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.overdue || false}
                    onChange={(e) => setFilters({ ...filters, overdue: e.target.checked })}
                    className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="ml-2 text-sm text-slate-700">Overdue</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={filters.upcoming || false}
                    onChange={(e) => setFilters({ ...filters, upcoming: e.target.checked })}
                    className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  />
                  <span className="ml-2 text-sm text-slate-700">Upcoming</span>
                </label>
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-slate-600 hover:text-gray-800"
            >
              Clear all filters
            </button>
          </div>
        </div>
      )}

      {/* Task Grid/Table */}
      {viewMode === 'grid' ? (
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedTasks.map((task) => (
            <div
              key={task.id}
              className={`bg-white overflow-hidden shadow rounded-lg border ${
                isOverdue(task) ? 'border-red-200 bg-red-50' : 
                isUpcoming(task) ? 'border-yellow-200 bg-yellow-50' : 
                'border-gray-200'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-slate-900 truncate">
                      {task.title}
                    </h3>
                    {task.description && (
                      <p className="mt-1 text-sm text-slate-500 line-clamp-2">
                        {task.description}
                      </p>
                    )}
                  </div>
                  <div className="ml-2 flex-shrink-0">
                    <div className="flex space-x-1">
                      {getPriorityBadge(task)}
                      {getTypeBadge(task)}
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center text-sm text-slate-500">
                    <CalendarIcon className="mr-1 h-4 w-4" />
                    {format(parseISO(task.dueDate), 'MMM dd, yyyy')}
                  </div>
                  {getStatusBadge(task)}
                </div>

                {task.assignedTo && (
                  <div className="mt-2 flex items-center text-sm text-slate-500">
                    <UserIcon className="mr-1 h-4 w-4" />
                    {task.assignedTo}
                  </div>
                )}

                {isOverdue(task) && (
                  <div className="mt-2 flex items-center text-sm text-red-600">
                    <ExclamationTriangleIcon className="mr-1 h-4 w-4" />
                    Overdue
                  </div>
                )}

                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => openViewModal(task)}
                    className="text-slate-400 hover:text-slate-600"
                    title="View details"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openEditModal(task)}
                    className="text-slate-400 hover:text-slate-600"
                    title="Edit task"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => openDeleteModal(task)}
                    className="text-slate-400 hover:text-red-600"
                    title="Delete task"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="mt-8 flex flex-col">
          <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
              <div className="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-slate-50/80">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Task
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Priority
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                        Assigned To
                      </th>
                      <th className="relative px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {paginatedTasks.map((task) => (
                      <tr key={task.id} className={isOverdue(task) ? 'bg-red-50' : ''}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                                <TagIcon className="h-5 w-5 text-slate-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-slate-900">
                                {task.title}
                              </div>
                              {task.description && (
                                <div className="text-sm text-slate-500 truncate max-w-xs">
                                  {task.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getTypeBadge(task)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPriorityBadge(task)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(task)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <CalendarIcon className="mr-1 h-4 w-4 text-slate-400" />
                            <span className={`text-sm ${isOverdue(task) ? 'text-red-600 font-medium' : 'text-slate-900'}`}>
                              {format(parseISO(task.dueDate), 'MMM dd, yyyy')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                          {task.assignedTo || 'Unassigned'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <button
                              onClick={() => openViewModal(task)}
                              className="text-cyan-600 hover:text-indigo-900"
                            >
                              View
                            </button>
                            <button
                              onClick={() => openEditModal(task)}
                              className="text-cyan-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => openDeleteModal(task)}
                              className="text-red-600 hover:text-red-900"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex items-center justify-between">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                <span className="font-medium">
                  {Math.min(currentPage * itemsPerPage, sortedTasks.length)}
                </span>{' '}
                of <span className="font-medium">{sortedTasks.length}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        page === currentPage
                          ? 'z-10 bg-cyan-50 border-cyan-500 text-cyan-600'
                          : 'bg-white border-gray-300 text-slate-500 hover:bg-slate-50/80'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-slate-500 hover:bg-slate-50/80 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {sortedTasks.length === 0 && !loading && (
        <EmptyState
          title="No tasks found"
          description={
            searchTerm || Object.keys(filters).some((key) => filters[key as keyof TaskFiltersState])
              ? 'Try adjusting your search or filter criteria.'
              : 'Get started by creating a new task.'
          }
          icon={<TagIcon className="h-6 w-6" />}
          action={
            !searchTerm && !Object.keys(filters).some((key) => filters[key as keyof TaskFiltersState]) ? (
              <button type="button" onClick={() => setShowAddModal(true)} className="btn btn-primary focus-pop">
                <PlusIcon className="h-5 w-5" />
                Add Task
              </button>
            ) : undefined
          }
        />
      )}

      {/* Modals */}
      <TaskModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddTask}
        mode="create"
      />

      <TaskModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSubmit={(data) => selectedTask && handleUpdateTask(selectedTask.id, data)}
        task={selectedTask}
        mode="edit"
      />

      <TaskViewModal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        task={selectedTask}
        onStatusChange={handleStatusChange}
        onEdit={openEditModal}
        onDelete={openDeleteModal}
      />

      <DeleteTaskModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteTask}
        task={selectedTask}
      />
    </div>
  );
} 